import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PropertySourcesSection } from '@/features/properties/PropertySourcesSection';
import { CaretLeftIcon, HouseLineIcon, WifiHighIcon, LockKeyIcon, DoorIcon, PhoneCallIcon, TrashIcon } from '@phosphor-icons/react';
import {
  createProperty,
  createPropertyFromUrl,
  deleteProperty,
  fetchProperties,
  mergeProperty,
  patchProperty,
  type PropertyRow,
} from '@/features/properties/api';
import { ApiError, Button, Card, CardContent, Input, Label, PageHeader, SkeletonRows, StatusChip, cn } from '@smena/ui';

interface PropertyFormState {
  title: string;
  city: string;
  address: string;
  checkinTime: string;
  checkoutTime: string;
  wifiSsid: string;
  wifiPass: string;
  lockCode: string;
  lockType: string;
  lockHelp: string;
  intercom: string;
  ukPhone: string;
  masterName: string;
  masterPhone: string;
  directions: string;
  parking: string;
}

function toForm(p: PropertyRow): PropertyFormState {
  const d = p.data;
  const parkingVal =
    typeof d.parking === 'string'
      ? d.parking
      : d.parking?.free || d.parking?.paid || '';
  return {
    title: p.title,
    city: p.city ?? '',
    address: d.address ?? '',
    checkinTime: d.checkinTime ?? '',
    checkoutTime: d.checkoutTime ?? '',
    wifiSsid: d.wifi?.ssid ?? '',
    wifiPass: d.wifi?.pass ?? '',
    lockCode: d.lock?.code ?? '',
    lockType: d.lock?.type ?? '',
    lockHelp: d.lock?.help ?? '',
    intercom: d.intercom ?? '',
    ukPhone: d.emergency?.ukPhone ?? '',
    masterName: d.emergency?.masterName ?? '',
    masterPhone: d.emergency?.masterPhone ?? '',
    directions: d.directions ?? '',
    parking: parkingVal,
  };
}

function fromForm(prev: PropertyRow, form: PropertyFormState) {
  return {
    title: form.title.trim(),
    city: form.city.trim() || null,
    data: {
      ...prev.data,
      address: form.address,
      checkinTime: form.checkinTime,
      checkoutTime: form.checkoutTime,
      wifi: { ssid: form.wifiSsid, pass: form.wifiPass },
      lock: { code: form.lockCode, type: form.lockType, help: form.lockHelp },
      intercom: form.intercom,
      emergency: {
        ukPhone: form.ukPhone,
        masterName: form.masterName,
        masterPhone: form.masterPhone,
      },
      directions: form.directions,
      parking: form.parking,
    },
  };
}

export function PropertiesPage() {
  const propsQ = useQuery({ queryKey: ['properties'], queryFn: fetchProperties });
  // Выбранный объект живёт в адресе, а не в состоянии компонента. Маршрут `/properties/:id`
  // был заведён в роутере, но страница его не читала: ссылка на конкретную квартиру
  // открывала пустую панель с «Выберите квартиру слева», и поделиться ею было нельзя.
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const selected = propsQ.data?.find((p) => p.id === routeId) ?? null;

  function select(id: string | null): void {
    navigate(id ? `/properties/${id}` : '/properties', { replace: !id });
  }

  // Объект удалён или ссылка ведёт на чужой id - возвращаем на список, а не показываем
  // пустоту с адресом несуществующей квартиры.
  useEffect(() => {
    if (routeId && propsQ.data && !selected) navigate('/properties', { replace: true });
  }, [routeId, propsQ.data, selected, navigate]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <PageHeader
            icon={<HouseLineIcon weight="bold" />}
            title="Объекты"
            description="Карточки квартир. Чем полнее заполнены, тем больше вопросов Smena закрывает без вас."
          />
        </div>
        <Button className="shrink-0" onClick={() => setAdding(true)}>
          Добавить объект
        </Button>
      </div>

      {adding ? (
        <AddPropertyDialog
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            select(id);
          }}
        />
      ) : null}

      {/* На мобильном одна колонка: две жёстких схлопывали список до нечитаемой полосы. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[320px_1fr]">
        {/* Список */}
        <Card className={cn('min-h-0 overflow-auto', routeId && 'hidden md:block')}>
          <div className="flex flex-col">
            {propsQ.isLoading ? (
              <SkeletonRows rows={4} />
            ) : propsQ.data?.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted">
                Пока ни одной квартиры. Нажмите «Добавить объект» - можно вставить ссылку на объявление, остальное Smena заполнит сама.
              </div>
            ) : (
              propsQ.data?.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => select(p.id)}
                  className={cn(
                    'flex items-start gap-3 border-b border-line/50 p-3 text-left transition-colors hover:bg-panel2',
                    routeId === p.id && 'bg-panel2',
                  )}
                >
                  <HouseLineIcon weight="regular" className="mt-0.5 size-5 shrink-0 text-muted" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{p.title}</div>
                    <div className="mt-0.5 text-xs text-muted">
                      {p.city ?? 'город не указан'} · {p.bookingsCount} брон.
                    </div>
                    {p.needsReview ? (
                      <div className="mt-1">
                        <StatusChip tone="amber">заполните карточку</StatusChip>
                      </div>
                    ) : null}
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Форма */}
        <Card className={cn('min-h-0 overflow-auto', !selected && 'hidden md:block')}>
          {!selected ? (
            <CardContent className="pt-6 text-center text-sm text-muted">
              Выберите квартиру слева
            </CardContent>
          ) : (
            <PropertyForm
              key={selected.id}
              property={selected}
              onDeleted={() => select(null)}
              onBack={() => select(null)}
            />
          )}
        </Card>
      </div>
    </div>
  );
}

function PropertyForm({
  property,
  onDeleted,
  onBack,
}: {
  property: PropertyRow;
  onDeleted: () => void;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<PropertyFormState>(() => toForm(property));
  const [dirty, setDirty] = useState(false);

  // При смене выбранного property обновляем локальное состояние; key={id} в родителе
  // фактически перезаливает компонент, но оставим effect как страховку.
  useEffect(() => {
    setForm(toForm(property));
    setDirty(false);
  }, [property]);

  function change<K extends keyof PropertyFormState>(k: K, v: string) {
    setForm((s) => ({ ...s, [k]: v }));
    setDirty(true);
  }

  const save = useMutation({
    mutationFn: () => patchProperty(property.id, fromForm(property, form)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['properties'] });
      setDirty(false);
      toast.success('Объект сохранён');
    },
    onError: (err: Error) => toast.error(`Ошибка: ${err.message}`),
  });

  return (
    <CardContent className="flex flex-col gap-5 py-5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <button
            type="button"
            onClick={onBack}
            className="-ml-1 shrink-0 self-start rounded-md p-1 text-muted hover:bg-panel2 hover:text-text md:hidden"
            aria-label="Назад к списку объектов"
          >
            <CaretLeftIcon weight="bold" className="size-5" />
          </button>
          <div className="min-w-0">
            <div className="text-lg font-semibold">{property.title}</div>
            <div className="text-xs text-muted">{property.bookingsCount} броней · id {property.id.slice(0, 8)}…</div>
            {/* Связь с RealtyCalendar живёт в «Источниках» - там же, где ссылки на
                объявления: это ровно тот же вопрос «откуда в эту карточку приходят данные». */}
            {property.needsReview ? (
              <div className="mt-1.5">
                <StatusChip tone="amber">заполните карточку</StatusChip>
              </div>
            ) : null}
          </div>
        </div>
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Сохраняем…' : dirty ? 'Сохранить' : 'Сохранено'}
        </Button>
      </div>

      <Section title="Базовое">
        <Field label="Название">
          <Input value={form.title} onChange={(e) => change('title', e.target.value)} />
        </Field>
        <Field label="Город">
          <Input value={form.city} onChange={(e) => change('city', e.target.value)} />
        </Field>
        <Field label="Адрес" full>
          <Input value={form.address} onChange={(e) => change('address', e.target.value)} />
        </Field>
        <Field label="Заезд с">
          <Input value={form.checkinTime} onChange={(e) => change('checkinTime', e.target.value)} placeholder="15:00" />
        </Field>
        <Field label="Выезд до">
          <Input value={form.checkoutTime} onChange={(e) => change('checkoutTime', e.target.value)} placeholder="12:00" />
        </Field>
      </Section>

      <Section title="Wi-Fi" icon={<WifiHighIcon weight="bold" />}>
        <Field label="Сеть (SSID)">
          <Input value={form.wifiSsid} onChange={(e) => change('wifiSsid', e.target.value)} />
        </Field>
        <Field label="Пароль">
          <Input value={form.wifiPass} onChange={(e) => change('wifiPass', e.target.value)} />
        </Field>
      </Section>

      <Section title="Замок" icon={<LockKeyIcon weight="bold" />}>
        <Field label="Тип замка">
          <Input value={form.lockType} onChange={(e) => change('lockType', e.target.value)} placeholder="умный замок или ключница" />
        </Field>
        <Field label="Код замка">
          <Input value={form.lockCode} onChange={(e) => change('lockCode', e.target.value)} placeholder="4821" />
        </Field>
        <Field label="Инструкция открытия" full>
          <textarea
            value={form.lockHelp}
            onChange={(e) => change('lockHelp', e.target.value)}
            rows={2}
            className="w-full rounded-md border border-line-control bg-input px-3 py-2 text-sm text-text"
          />
        </Field>
      </Section>

      <Section title="Домофон" icon={<DoorIcon weight="bold" />}>
        <Field label="Номер квартиры / код" full>
          <Input value={form.intercom} onChange={(e) => change('intercom', e.target.value)} />
        </Field>
      </Section>

      <Section title="Экстренные контакты" icon={<PhoneCallIcon weight="bold" />}>
        <Field label="Телефон УК">
          <Input value={form.ukPhone} onChange={(e) => change('ukPhone', e.target.value)} />
        </Field>
        <Field label="Имя мастера">
          <Input value={form.masterName} onChange={(e) => change('masterName', e.target.value)} />
        </Field>
        <Field label="Телефон мастера">
          <Input value={form.masterPhone} onChange={(e) => change('masterPhone', e.target.value)} />
        </Field>
      </Section>

      <Section title="Как проехать">
        <Field label="Проезд" full>
          <textarea
            value={form.directions}
            onChange={(e) => change('directions', e.target.value)}
            rows={2}
            className="w-full rounded-md border border-line-control bg-input px-3 py-2 text-sm text-text"
          />
        </Field>
        <Field label="Парковка" full>
          <textarea
            value={form.parking}
            onChange={(e) => change('parking', e.target.value)}
            rows={2}
            className="w-full rounded-md border border-line-control bg-input px-3 py-2 text-sm text-text"
          />
        </Field>
      </Section>

      <PropertySourcesSection propertyId={property.id} rcObjectId={property.rcObjectId} />

      <MergeSection property={property} />

      <DangerZone property={property} onDeleted={onDeleted} />

      <div className="text-xs text-muted">
        Удобства, спальные места, правила и фото добавим в следующей версии.
      </div>
    </CardContent>
  );
}

/**
 * Объединение дублей.
 *
 * Зачем. RealtyCalendar не присылает список объектов - `realty_id` мы узнаём только из первого
 * события вебхука. Если к этому моменту квартира уже заведена в панели руками, автозаведение
 * создаёт вторую карточку того же жилья. Развести их было нечем: дубль не удалить (на нём уже
 * брони), перенести брони некуда.
 *
 * Направление фиксированное: выбранный в списке объект поглощается ЭТОЙ карточкой. Так человек
 * стоит на заполненной карточке и подтягивает в неё пустую, а не наоборот - обратный порядок
 * означал бы потерю анкеты, которую он заполнял руками.
 */
function MergeSection({ property }: { property: PropertyRow }) {
  const qc = useQueryClient();
  const [sourceId, setSourceId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const propsQ = useQuery({ queryKey: ['properties'], queryFn: fetchProperties });
  const others = (propsQ.data ?? []).filter((p) => p.id !== property.id);
  const source = others.find((p) => p.id === sourceId) ?? null;

  const merge = useMutation({
    mutationFn: () => mergeProperty(property.id, sourceId),
    onSuccess: (r) => {
      setConfirming(false);
      setSourceId('');
      const bits = [
        r.moved.bookings ? `броней: ${r.moved.bookings}` : null,
        r.moved.rcMappings ? 'привязка к RealtyCalendar' : null,
        r.moved.listings ? `объявлений: ${r.moved.listings}` : null,
      ].filter(Boolean);
      toast.success(`Объекты объединены${bits.length ? ` (${bits.join(', ')})` : ''}`);
      void qc.invalidateQueries({ queryKey: ['properties'] });
      void qc.invalidateQueries({ queryKey: ['property-sources', property.id] });
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
    },
    // 409 - пересечение дат, текст с конкретными датами приходит с сервера.
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось объединить объекты'),
  });

  if (others.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-4">
      <div className="text-sm font-semibold uppercase tracking-wider text-muted">
        Объединить с дублем
      </div>
      <p className="text-xs text-muted">
        Если эта же квартира завелась в списке второй карточкой - например, приехала из
        RealtyCalendar под своим названием, - выберите её здесь. Брони, диалоги гостей, привязка
        к RealtyCalendar, ссылки на объявления и база знаний переедут сюда, лишняя карточка
        удалится.
      </p>

      <select
        value={sourceId}
        onChange={(e) => {
          setSourceId(e.target.value);
          setConfirming(false);
        }}
        aria-label="Объект-дубль"
        className="w-full rounded-md border border-line-control bg-input px-3 py-2 text-sm text-text"
      >
        <option value="">Выберите дубль…</option>
        {others.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title}
            {p.city ? ` · ${p.city}` : ''} · {p.bookingsCount} брон.
            {p.rcObjectId ? ` · лот ${p.rcObjectId}` : ''}
          </option>
        ))}
      </select>

      {source && !confirming ? (
        <div>
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            Объединить
          </Button>
        </div>
      ) : null}

      {source && confirming ? (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-amber">
            Карточка «{source.title}» будет удалена. Сюда переедут её брони
            {source.bookingsCount ? ` (${source.bookingsCount})` : ''}
            {source.rcObjectId ? `, привязка к RealtyCalendar (лот ${source.rcObjectId})` : ''},
            диалоги гостей, ссылки на объявления и база знаний. Анкета этой карточки не изменится,
            анкета дубля не сохранится. Факты базы знаний с одинаковыми ключами останутся от этой
            карточки.
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={merge.isPending}
              onClick={() => merge.mutate()}
            >
              {merge.isPending ? 'Объединяем…' : 'Всё равно объединить'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Удаление объекта.
 *
 * Ручка `DELETE /api/staff/properties/:id` была с самого начала, а кнопки не было ни одной:
 * завести квартиру можно, убрать - нет. Особенно больно после дубля из парсера - лишний
 * объект оставался в списке навсегда.
 *
 * Внизу и отдельным блоком: рядом с «Сохранить» её слишком легко нажать мимо.
 */
function DangerZone({ property, onDeleted }: { property: PropertyRow; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const del = useMutation({
    mutationFn: () => deleteProperty(property.id),
    onSuccess: () => {
      toast.success(`Объект «${property.title}» удалён`);
      void qc.invalidateQueries({ queryKey: ['properties'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
      onDeleted();
    },
    // 409 от сервера - это «на квартире есть брони», и текст оттуда осмысленный.
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Не удалось удалить объект'),
  });

  const blocked = property.bookingsCount > 0;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-4">
      <div className="text-sm font-semibold uppercase tracking-wider text-muted">Удаление</div>
      {blocked ? (
        <p className="text-xs text-muted">
          Удалить нельзя: на объекте {property.bookingsCount}{' '}
          {pluralBookings(property.bookingsCount)}. Брони без объекта осталась бы без адреса и
          анкеты, а диалоги гостей - без контекста.
        </p>
      ) : !confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            <TrashIcon weight="bold" className="size-4" /> Удалить объект
          </Button>
          <span className="text-xs text-muted">броней нет, удаление безопасно</span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-xs text-amber">
            Объект и его анкета удалятся навсегда - Wi-Fi, коды замков, привязанные ссылки.
            Восстановить будет нечем.
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={del.isPending}
              onClick={() => del.mutate()}
            >
              {del.isPending ? 'Удаляем…' : 'Всё равно удалить'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Отмена
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function pluralBookings(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'бронь';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'брони';
  return 'броней';
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line bg-panel2/40 p-4">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted">
        {icon ? <span className="text-accent-fg">{icon}</span> : null}
        {title}
      </div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-1', full && 'col-span-2')}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}

/**
 * Добавление объекта: по ссылке на объявление (парсер) или вручную.
 *
 * Парсер-путь идёт в /listings/create-property, а НЕ в /listings/import: второй
 * перезаписывает первый объект аккаунта (наследие демо-стенда) и затёр бы уже
 * заведённую квартиру. Ручной путь оставляет анкету пустой - её дозаполняют в форме справа.
 */
function AddPropertyDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'url' | 'manual'>('url');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [city, setCity] = useState('');
  const [error, setError] = useState<string | null>(null);

  const done = (id: string, warnings?: { message?: string }[]) => {
    void qc.invalidateQueries({ queryKey: ['properties'] });
    // Предупреждения парсера показываем явно: часть анкеты почти всегда остаётся пустой,
    // и молчать об этом - значит выпустить объект с дырами в данных для бота.
    if (warnings?.length) {
      toast.warning(`Объект создан, но парсер не всё нашёл (${warnings.length}) - проверьте анкету`);
    } else {
      toast.success('Объект создан');
    }
    onCreated(id);
  };

  const fromUrl = useMutation({
    mutationFn: () => createPropertyFromUrl(url.trim()),
    onSuccess: (r) => done(r.propertyId, r.warnings),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Не удалось разобрать объявление'),
  });

  const manual = useMutation({
    mutationFn: () => createProperty({ title: title.trim(), city: city.trim() || null }),
    onSuccess: (r) => done(r.id),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Не удалось создать объект'),
  });

  const busy = fromUrl.isPending || manual.isPending;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-1 rounded-md border border-line p-0.5">
            {(['url', 'manual'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setError(null);
                }}
                className={cn(
                  'rounded px-3 py-1.5 text-sm transition-colors',
                  mode === m ? 'bg-accent text-white' : 'text-muted hover:text-text',
                )}
              >
                {m === 'url' ? 'По ссылке' : 'Вручную'}
              </button>
            ))}
          </div>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
        </div>

        {mode === 'url' ? (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              fromUrl.mutate();
            }}
          >
            <Label htmlFor="listing-url">Ссылка на объявление</Label>
            <div className="flex gap-2">
              <Input
                id="listing-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.avito.ru/… или sutochno.ru/…"
                required
              />
              <Button type="submit" disabled={busy || !url.trim()}>
                {fromUrl.isPending ? 'Разбираем…' : 'Создать'}
              </Button>
            </div>
            <p className="text-xs text-muted">
              Заголовок, город и анкету заполним из объявления. Avito и Яндекс.Путешествия
              требуют браузерного парсера - если он выключен, заводите вручную.
            </p>
          </form>
        ) : (
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              manual.mutate();
            }}
          >
            <Label htmlFor="new-title">Название</Label>
            <Input
              id="new-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="1-комн. квартира на Ленина"
              required
            />
            <Label htmlFor="new-city">Город</Label>
            <div className="flex gap-2">
              <Input
                id="new-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Екатеринбург"
              />
              <Button type="submit" disabled={busy || !title.trim()}>
                {manual.isPending ? 'Создаём…' : 'Создать'}
              </Button>
            </div>
            <p className="text-xs text-muted">
              Пустой объект - анкету (Wi-Fi, замок, домофон) дозаполните в форме справа.
            </p>
          </form>
        )}

        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </CardContent>
    </Card>
  );
}
